import { Info, Globe } from 'lucide-preact'
import { type WebViewDto } from '@/api/webs'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { useOnlineEventSpeakers } from '@/hooks/useOnlineEventSpeakers'
import { useOnlineEventForm, type SpeakerUiWithPic } from '@/hooks/useOnlineEventForm'
import { useBigMarkerIntegration } from '@/hooks/useBigMarkerIntegration'
import { useCompany } from '@/hooks/useCompany'
import { SpeakerListSection } from './SpeakerListSection'
import { AgendaSection } from './AgendaSection'
import { OnlineEventDetailsCard } from './OnlineEventDetailsCard'
import { OverviewCard } from './OverviewCard'
import { SpeakerEditors } from './SpeakerEditors'

export function OnlineEventPanel({ web, onPatchWeb, isSignedOff, syncing, routeToTab }: { web: WebViewDto; onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void; isSignedOff?: boolean; syncing?: boolean; routeToTab?: (tab: WebTabKey) => void }) {
  const evt = web.OnlineEvent
  const { companyId } = useCompany()
  const { hasBigMarker, integrationError, integrationName } = useBigMarkerIntegration(companyId ?? undefined)
  const speakers: SpeakerUiWithPic[] = (evt?.Speakers as SpeakerUiWithPic[]) || []

  const speakerHook = useOnlineEventSpeakers({ web, onPatchWeb })
  const {
    editingDetails, setEditingDetails, savingDetails, setSavingDetails,
    editingOverview, setEditingOverview, savingOverview, setSavingOverview,
    anySectionEditing,
    register, watch, setValue, getFieldState, formState, control,
    agendaFields, append, remove, move,
    saving, hasDirtyEmpty, normalizedInitial, agendaChanged,
    editingAgenda, setEditingAgenda,
    saveAgenda, cancelAgendaEdit,
  } = useOnlineEventForm({ web, onPatchWeb, speakers, showAddParticipant: speakerHook.showAddParticipant, showEditModal: speakerHook.showEditModal })

  return (
    <>
      {isSignedOff && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center gap-3 text-sm text-blue-800 dark:text-blue-300 mb-6">
          <Info className="w-5 h-5 flex-shrink-0" />
          <span>This web is signed off. Reopen from the header above to make changes to event details, speakers, and agenda.</span>
        </div>
      )}
      {/* min-w-0 on both columns: grid items default to min-width:auto, so the nowrap
          speaker title in the right column would otherwise widen its track past 1.2fr
          and squeeze Overview/Event details. */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1.2fr] gap-6">
        <div className="min-w-0 space-y-6">
          <OverviewCard
            onlineEvent={evt} web={web}
            editingOverview={editingOverview} savingOverview={savingOverview}
            editDisabled={isSignedOff || syncing || anySectionEditing}
            onEditStart={() => setEditingOverview(true)} onEditEnd={() => setEditingOverview(false)}
            onSavingChange={setSavingOverview} onPatchWeb={onPatchWeb}
            watch={watch} setValue={setValue} control={control}
          />
          <OnlineEventDetailsCard
            evt={evt} web={web}
            editing={editingDetails} saving={savingDetails}
            editDisabled={anySectionEditing || isSignedOff || syncing}
            syncing={syncing} integrationError={integrationError}
            integrationName={integrationName}
            onEdit={() => setEditingDetails(true)} onCancel={() => setEditingDetails(false)}
            onSaved={() => setEditingDetails(false)} onSaving={setSavingDetails}
            onPatchWeb={onPatchWeb}
            control={control} register={register} watch={watch} setValue={setValue}
            getFieldState={getFieldState} formState={formState}
          />
        </div>
        <div className="min-w-0 space-y-4">
          <SpeakerListSection
            speakers={speakers} isSignedOff={isSignedOff} syncing={syncing}
            speakersSyncReviewNeeded={web.OnlineEvent?.SpeakersSyncReviewNeeded}
            onAddParticipant={() => speakerHook.setShowAddParticipant(true)}
            onEditSpeaker={speakerHook.openEdit}
            onDeleteSpeaker={speakerHook.setDeleteTarget}
            onSaveReorder={speakerHook.handleSaveReorder}
          />
          <AgendaSection
            editing={editingAgenda}
            saving={saving}
            editDisabled={isSignedOff || syncing || anySectionEditing || !!evt?.ConferenceId}
            externallyManaged={!!evt?.ConferenceId}
            hasBigMarker={hasBigMarker}
            integrationName={integrationName}
            initialItems={normalizedInitial}
            agendaChanged={agendaChanged}
            hasDirtyEmpty={hasDirtyEmpty}
            agendaFields={agendaFields}
            control={control}
            append={append} remove={remove} move={move}
            onEdit={() => setEditingAgenda(true)}
            onSave={saveAgenda}
            onCancel={cancelAgendaEdit}
          />
        </div>
      </div>
      <SpeakerEditors
        workspaceId={web.WorkspaceId} webId={web.Id} campaignId={web.CampaignId}
        {...speakerHook}
      />
      {isSignedOff && routeToTab && (
        <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Landing Page</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Generate a landing page for this event to drive registrations.
              </p>
            </div>
            <button
              type="button"
              onClick={() => routeToTab('media')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Generate Landing Page
            </button>
          </div>
        </div>
      )}
    </>
  )
}
